import type { ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import type { Leaves } from '@src/utils/types';
import { AgGridCurrency, AgGridSecurity, type AgGridCurrencyProps, type AgGridSecurityProps } from '../../AgGrid';
import { baseColumn } from './baseColumn';
import { resolveBlotterAssetIsCurrency } from './resolveBlotterAssetIsCurrency';
import type { ColDefFactory, Column } from './types';

export interface AssetColumnProps<T extends object = any>
  extends Partial<AgGridCurrencyProps>, Partial<AgGridSecurityProps> {
  assetTypeField?: Leaves<T>;
  /** Customize the display symbol used for searching and exports. */
  formatDisplayValue?: (displaySymbol: string, params: ValueFormatterParams<T, string>) => string;
}

/**
 * Column that represents BOTH Securities and Currencies, renders both well
 *
 * - TODO - it does NOT yet support certain properties that the 'security' and 'currency' columns do, such as securityCurrencyField
 */
export const asset: ColDefFactory<Column<AssetColumnProps>> = (column => ({
  ...baseColumn(column),
  cellRendererParams: column.params,
  cellRenderer: (params: ICellRendererParams) => {
    const assetTypeProp = column.params?.assetTypeField || '';
    const isCurrency = resolveBlotterAssetIsCurrency(params, assetTypeProp || undefined);
    return isCurrency ? (
      <AgGridCurrency {...column.params} {...params} />
    ) : (
      <AgGridSecurity {...column.params} {...params} />
    );
  },
  valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
    if (!params.value) {
      return '';
    }

    const security = params.context.current.securitiesBySymbol?.get(params.value);
    const displaySymbol = security?.DisplaySymbol ?? security?.Symbol ?? params.value;
    return column.params?.formatDisplayValue?.(displaySymbol, params) ?? displaySymbol;
  },
})) satisfies ColDefFactory<Column<AssetColumnProps>>;
